import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PropTypes } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { useTableFactories } from '@ge/components/table/use-table-factories';
import { Placeholders, DayJs } from '@ge/models/constants';

import { ReportWidgetTable } from '../../tables/report-widget-table';

import {
  uptowerRepairDamagesColumnsDefs,
  UptowerRepairDamagesColumns,
} from './uptowerRepair-damages-cols';

dayjs.extend(isoWeek);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const RepairDamagesTableWidget = ({
  name,
  columns,
  data = [],
  isEditing,
  isInteractive,
  onRowSelectionChange,
}) => {
  const { t, ready } = useTranslation(['reporting.widgets']);
  // initialize all rows being selected
  const [rowsSelected] = useState(() => data.map(({ id }) => id));

  useEffect(() => {
    onRowSelectionChange(rowsSelected);
  }, [onRowSelectionChange, rowsSelected]);

  const cellValueMapFn = useCallback(
    (cellValue) => {
      let rulStartDate = null;
      if (cellValue.rulStartDate) {
        const date = dayjs(cellValue.rulStartDate).format('DD MMM YY');
        rulStartDate = date !== DayJs.INVALID_DATE && date;
      }
      return {
        [UptowerRepairDamagesColumns.SITE]: cellValue.site ? cellValue.site : Placeholders.DASH,
        [UptowerRepairDamagesColumns.ASSET]: cellValue.asset ? cellValue.asset : Placeholders.DASH,
        [UptowerRepairDamagesColumns.MODEL]: cellValue.model ? cellValue.model : Placeholders.DASH,
        [UptowerRepairDamagesColumns.COMPONENT]: cellValue.component
          ? cellValue.component
          : Placeholders.DASH,
        [UptowerRepairDamagesColumns.LOCATION]: cellValue.location
          ? cellValue.location
          : Placeholders.DASH,
        [UptowerRepairDamagesColumns.DAMAGE]: cellValue.damage
          ? cellValue.damage
          : Placeholders.DASH,
        [UptowerRepairDamagesColumns.PART]: cellValue.part ? cellValue.part : Placeholders.DASH,
        [UptowerRepairDamagesColumns.CATEGORY]: cellValue.category
          ? cellValue.category
          : Placeholders.DASH,
        [UptowerRepairDamagesColumns.MANUFACTURER]: cellValue.manufacturer
          ? cellValue.manufacturer
          : Placeholders.DASH,
        [UptowerRepairDamagesColumns.RUL_START_DATE]: rulStartDate
          ? rulStartDate
          : Placeholders.DASH,
        [UptowerRepairDamagesColumns.P90]: cellValue.p90 ? cellValue.p90 : Placeholders.DASH,
      };
    },
    [t],
  );

  // Table factories
  const [columnGroupFactory, columnFactory, cellFactory] = useTableFactories({
    t,
    columnDefs: uptowerRepairDamagesColumnsDefs,
    cellValueMapFn,
  });

  if (!ready) {
    return <Loader />;
  }

  return (
    <Container>
      <ReportWidgetTable
        key={name}
        compressed
        noTitles
        scrollable={isInteractive}
        transparent
        columnGroupFactory={columnGroupFactory}
        columnFactory={columnFactory}
        cellFactory={cellFactory}
        columns={columns}
        values={data}
        onValueSelect={() => null}
        rowsSelected={rowsSelected}
        rowKeyProperty={'id'}
        isEditing={isEditing}
        variableHeight={true}
        vAlign={'middle'}
      />
    </Container>
  );
};

RepairDamagesTableWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  columns: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isEditing: PropTypes.bool,
  isInteractive: PropTypes.bool,
  onRowSelectionChange: PropTypes.func.isRequired,
};
